import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCategoriesThunk } from '../../data/thunks/projectDataThunk';
import { AppDispatch, RootState } from '../../data/store';
import { useParams } from 'react-router-dom';
import {
	Box,
	Typography,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Switch,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ProjectDataCategory } from '../../data/models/projectData';
import { updateConfigurationThunk } from '../../data/thunks/projectConfigurationThunk';

export default function ProjectTypeData() {
	const dispatch = useDispatch<AppDispatch>();
	const { configurationId } = useParams();

	const { categories, initialized } = useSelector(
		(state: RootState) => state.projectData
	);

	const { configuration } = useSelector(
		(state: RootState) => state.projectConfigurationState
	);

	useEffect(() => {
		if (configurationId) {
			dispatch(fetchCategoriesThunk({}));
		}
	}, [configurationId, dispatch]);

	const handleCategoryToggle = (categoryId: string, enabled: boolean) => {
		if (configurationId) {
			if (enabled) {
				dispatch(
					updateConfigurationThunk({
						id: configurationId,
						addProjectDataCategories: [categoryId],
					})
				);
			} else {
				dispatch(
					updateConfigurationThunk({
						id: configurationId,
						removeProjectDataCategories: [categoryId],
					})
				);
			}
		}
	};

	if (!initialized) {
		return (
			<Box sx={{ p: 2 }}>
				<Typography variant="body1" color="text.secondary">
					Loading data categories...
				</Typography>
			</Box>
		);
	}

	if (!categories || categories.length === 0) {
		return (
			<Box sx={{ p: 2 }}>
				<Typography variant="body1" color="text.secondary">
					No data categories configured for this project type.
				</Typography>
			</Box>
		);
	}

	return (
		<Box
			sx={{
				width: '100%',
				mt: 2,
				height: 'calc(100vh - 228px)',
				overflow: 'auto',
			}}
		>
			{categories.map((category: ProjectDataCategory) => {
				const isCategoryEnabled =
					configuration.projectDataCategoryIds?.includes(category.id);

				return (
					<Accordion
						key={category.id}
						sx={{
							mb: 1,
							'&:before': {
								display: 'none',
							},
							backgroundColor: 'background.paper',
							border: 'none',
							borderRadius: '8px !important',
							'&.Mui-expanded': {
								margin: '0 0 8px 0',
							},
						}}
					>
						<AccordionSummary
							expandIcon={<ExpandMoreIcon />}
							aria-controls={`${category.id}-content`}
							id={`${category.id}-header`}
							sx={{
								flexDirection: 'row-reverse',
								'& .MuiAccordionSummary-expandIconWrapper.Mui-expanded':
									{
										transform: 'rotate(180deg)',
									},
								'& .MuiAccordionSummary-content': {
									marginLeft: (theme) => theme.spacing(1),
									alignItems: 'center',
									justifyContent: 'space-between',
								},
							}}
						>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'space-between',
									width: '100%',
								}}
							>
								<Typography
									variant="body1"
									sx={{
										fontWeight: 500,
										textTransform: 'uppercase',
										fontSize: '0.875rem',
										letterSpacing: '0.5px',
									}}
								>
									{category.name}
								</Typography>
								<Box
									sx={{
										display: 'flex',
										alignItems: 'center',
										gap: 1,
									}}
								>
									<Typography
										variant="body2"
										sx={{
											color: isCategoryEnabled
												? 'primary.main'
												: 'error.main',
											fontWeight: 500,
											fontSize: '0.75rem',
										}}
									>
										{isCategoryEnabled
											? 'Enabled'
											: 'Disabled'}
									</Typography>
									<Switch
										checked={isCategoryEnabled}
										onChange={(event) => {
											event.stopPropagation();
											handleCategoryToggle(
												category.id,
												event.target.checked
											);
										}}
										onClick={(event) =>
											event.stopPropagation()
										}
										size="small"
										sx={{
											'& .MuiSwitch-switchBase.Mui-checked':
												{
													color: 'primary.main',
												},
											'& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
												{
													backgroundColor:
														'primary.main',
												},
										}}
									/>
								</Box>
							</Box>
						</AccordionSummary>
						<AccordionDetails sx={{ pt: 0, pb: 2 }}>
							<Box sx={{ pl: 2 }}>
								{category.projectDataPointDefinitions &&
								category.projectDataPointDefinitions.length >
									0 ? (
									[...category.projectDataPointDefinitions]
										.sort(
											(a, b) =>
												a.displayOrder - b.displayOrder
										)
										.map((definition) => (
											<Box
												key={definition.id}
												sx={{
													py: 1,
													borderBottom: '1px solid',
													borderColor: 'divider',
													'&:last-child': {
														borderBottom: 'none',
													},
												}}
											>
												<Typography
													variant="body2"
													sx={{
														color: 'text.primary',
														fontWeight: 400,
														textTransform:
															'uppercase',
														fontSize: '0.75rem',
														letterSpacing: '0.5px',
													}}
												>
													{definition.label ||
														definition.name}
												</Typography>
												{definition.description && (
													<Typography
														variant="caption"
														sx={{
															color: 'text.secondary',
															mt: 0.5,
															display: 'block',
														}}
													>
														{definition.description}
													</Typography>
												)}
											</Box>
										))
								) : (
									<Typography
										variant="body2"
										color="text.secondary"
									>
										No data points configured for this
										category.
									</Typography>
								)}
							</Box>
						</AccordionDetails>
					</Accordion>
				);
			})}
		</Box>
	);
}
